import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getListingImage } from './listingPlaceholder';

// Listvy card photos: up to MAX_CARD_IMAGES in a strip that snaps one photo at a time. Swipe on touch
// screens, scroll sideways on a trackpad, or use the arrows that show on hover. Only the photo on show and
// the next one are loaded, so a page of cards doesn't fetch every photo at once.
const MAX_CARD_IMAGES = 6;

const getCardImages = (listing) => {
    const first = getListingImage(listing);
    const seen = new Set([first]);
    const images = [first];
    (Array.isArray(listing?.images) ? listing.images : []).forEach((url) => {
        const value = String(url || '').trim();
        if (!value || seen.has(value) || images.length >= MAX_CARD_IMAGES) return;
        seen.add(value);
        images.push(value);
    });
    return images;
};

const ListingCardGallery = ({ listing, alt, eager = false, highPriority = false, swedish = true, children }) => {
    const images = getCardImages(listing);
    const trackRef = useRef(null);
    const frameRef = useRef(0);
    const [index, setIndex] = useState(0);
    const count = images.length;

    // Back to the first photo when the card shows another listing.
    const firstImage = images[0];
    useEffect(() => {
        setIndex(0);
        if (trackRef.current) trackRef.current.scrollLeft = 0;
    }, [firstImage]);

    useEffect(() => () => window.cancelAnimationFrame(frameRef.current), []);

    const handleScroll = useCallback(() => {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = window.requestAnimationFrame(() => {
            const track = trackRef.current;
            if (!track || !track.clientWidth) return;
            setIndex(Math.min(count - 1, Math.max(0, Math.round(track.scrollLeft / track.clientWidth))));
        });
    }, [count]);

    const step = (event, direction) => {
        event.stopPropagation();
        const track = trackRef.current;
        if (!track) return;
        const target = Math.min(count - 1, Math.max(0, index + direction));
        track.scrollTo({ left: target * track.clientWidth, behavior: 'smooth' });
    };

    return (
        <div className="listing-card-gallery">
            <div className="listing-card-gallery-track" ref={trackRef} onScroll={count > 1 ? handleScroll : undefined}>
                {images.map((url, imageIndex) => (
                    <div className="listing-card-gallery-slide" key={url}>
                        {imageIndex <= index + 1 && (
                            <img
                                src={url}
                                alt={imageIndex === 0 ? alt : ''}
                                loading={imageIndex === 0 && eager ? 'eager' : 'lazy'}
                                fetchPriority={imageIndex === 0 && highPriority ? 'high' : 'auto'}
                                draggable={false}
                            />
                        )}
                    </div>
                ))}
            </div>
            {count > 1 && (
                <>
                    <button
                        type="button"
                        className="listing-card-gallery-arrow is-prev"
                        onClick={(event) => step(event, -1)}
                        onDoubleClick={(event) => event.stopPropagation()}
                        disabled={index === 0}
                        aria-label={swedish ? 'Föregående bild' : 'Previous photo'}
                    >
                        <span aria-hidden="true">‹</span>
                    </button>
                    <button
                        type="button"
                        className="listing-card-gallery-arrow is-next"
                        onClick={(event) => step(event, 1)}
                        onDoubleClick={(event) => event.stopPropagation()}
                        disabled={index >= count - 1}
                        aria-label={swedish ? 'Nästa bild' : 'Next photo'}
                    >
                        <span aria-hidden="true">›</span>
                    </button>
                    <div className="listing-card-gallery-dots" aria-hidden="true">
                        {images.map((url, dotIndex) => (
                            <i key={url} className={dotIndex === index ? 'is-active' : ''} />
                        ))}
                    </div>
                </>
            )}
            {children}
        </div>
    );
};

export default ListingCardGallery;
